// Dedicated Aura Score page. Shows a scanning animation over the user's
// generated try-on photo while we call the `aura-score` edge function,
// then renders the full audit (ring + sub-scores + verdict + tips).
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Sparkles, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/hooks/useCredits";
import { supabase } from "@/integrations/supabase/client";
import { readEdgeError } from "@/lib/edge-error";
import { useSignedImage } from "@/hooks/useSignedImage";
import { AuraScoreRing } from "@/components/AuraScoreRing";
import { BuyCreditsDialog } from "@/components/BuyCreditsDialog";
import { PaywallModal } from "@/components/PaywallModal";
import { usePaywallTriggers, type PaywallVariant } from "@/hooks/usePaywallTriggers";
import { useFreeTierGate } from "@/contexts/FreeTierGateContext";
import { cn } from "@/lib/utils";
import type { AuraData } from "@/components/AuraScoreCard";
import { SEO } from "@/components/SEO";
import { MobileShell } from "@/components/mobile";
import { useIsMobile } from "@/hooks/use-mobile";

interface TryOnRow {
  id: string;
  user_id: string;
  generated_image_url: string | null;
  aura_score: number | null;
  aura_silhouette: number | null;
  aura_proportions: number | null;
  aura_color_season: number | null;
  aura_statement: number | null;
  aura_photo_readiness: number | null;
  aura_verdict: string | null;
  aura_tips: string[] | null;
}

const SUB_LABELS: Array<[keyof AuraData, string]> = [
  ["aura_silhouette", "Silhouette"],
  ["aura_proportions", "Proportions"],
  ["aura_color_season", "Color season"],
  ["aura_statement", "Statement factor"],
  ["aura_photo_readiness", "Photo-readiness"],
];

const SCAN_STAGES = [
  "Reading silhouette…",
  "Mapping proportions…",
  "Analyzing color season…",
  "Gauging statement factor…",
  "Checking photo-readiness…",
  "Composing your aura…",
];

const AuraScore = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { credits, refresh } = useCredits();
  const { checkBeforeTryon } = usePaywallTriggers();
  const { runGate } = useFreeTierGate();
  const isMobile = useIsMobile();

  const [tryon, setTryon] = useState<TryOnRow | null>(null);
  const [aura, setAura] = useState<AuraData | null>(null);
  const [scanning, setScanning] = useState(false);
  const [stage, setStage] = useState(0);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyDefaultPack, setBuyDefaultPack] = useState<string | undefined>(undefined);
  const [paywall, setPaywall] = useState<PaywallVariant | null>(null);
  const paywallResolverRef = useRef<((bought: boolean) => void) | null>(null);
  const showPaywallAndWait = (variant: PaywallVariant): Promise<boolean> => {
    setPaywall(variant);
    return new Promise<boolean>((resolve) => { paywallResolverRef.current = resolve; });
  };
  const handlePaywallDismiss = () => {
    setPaywall(null);
    const r = paywallResolverRef.current; paywallResolverRef.current = null;
    r?.(false);
  };
  const handlePaywallBuy = (packId: "mini" | "value") => {
    setBuyDefaultPack(packId);
    setBuyOpen(true);
    setPaywall(null);
    const r = paywallResolverRef.current; paywallResolverRef.current = null;
    r?.(true);
  };
  const startedRef = useRef(false);

  // SEO handled by <SEO /> component in render.

  // Load the tryon row.
  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data, error } = await supabase
        .from("tryons")
        .select(
          "id, user_id, generated_image_url, aura_score, aura_silhouette, aura_proportions, aura_color_season, aura_statement, aura_photo_readiness, aura_verdict, aura_tips",
        )
        .eq("id", id)
        .maybeSingle();
      if (error) {
        toast({ title: "Couldn't load result", description: error.message, variant: "destructive" });
        return;
      }
      if (!data) return;
      const row = data as unknown as TryOnRow;
      setTryon(row);
      if (typeof row.aura_score === "number") {
        // Cached — show immediately, no scan.
        setAura({
          aura_score: row.aura_score,
          aura_silhouette: row.aura_silhouette,
          aura_proportions: row.aura_proportions,
          aura_color_season: row.aura_color_season,
          aura_statement: row.aura_statement,
          aura_photo_readiness: row.aura_photo_readiness,
          aura_verdict: row.aura_verdict,
          aura_tips: row.aura_tips,
        });
      }
    })();
  }, [id, toast]);

  const photoUrl = useSignedImage("tryon-results", tryon?.generated_image_url);

  // Cycle through scanning stage labels.
  useEffect(() => {
    if (!scanning) return;
    const t = setInterval(() => {
      setStage((s) => (s + 1) % SCAN_STAGES.length);
    }, 1100);
    return () => clearInterval(t);
  }, [scanning]);

  const runAura = async () => {
    if (!tryon || scanning) return;
    // Aura costs 0.10 credits.
    if ((credits ?? 0) < 0.10) {
      setBuyOpen(true);
      return;
    }
    // Emotional paywall — "Maybe later" falls through to the ad-gate so free
    // users can still earn this credit by watching an ad.
    const trig = checkBeforeTryon();
    if (trig) {
      const bought = await showPaywallAndWait(trig.variant);
      if (bought) return;
    }
    // Free-tier ad-gate (Android only). Aura also counts toward the lifetime 3.
    const allowed = await runGate();
    if (!allowed) return;
    setScanning(true);
    setStage(0);
    try {
      const { data, error } = await supabase.functions.invoke("aura-score", {
        body: { tryonId: tryon.id },
      });
      if (error) {
        const ctx = (error as { context?: { status?: number } }).context;
        if (ctx?.status === 402) {
          setBuyOpen(true);
          return;
        }
        if (ctx?.status === 429) {
          toast({ title: "Rate limited", description: "Try again in a moment.", variant: "destructive" });
          return;
        }
        throw error;
      }
      setAura(data as AuraData);
      await refresh();
      toast({ title: "Aura score ready ✨" });
    } catch (e) {
      const { message } = await readEdgeError(e);
      toast({
        title: "Couldn't compute aura",
        description: message,
        variant: "destructive",
      });
    } finally {
      setScanning(false);
    }
  };

  // Auto-start the scan once the photo + tryon are loaded and we don't already have a result.
  useEffect(() => {
    if (startedRef.current) return;
    if (!tryon || !user) return;
    if (aura) return; // cached
    if (!tryon.generated_image_url) return;
    startedRef.current = true;
    runAura();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tryon, user, aura]);

  const headerSubtitle = useMemo(() => {
    if (aura) return "Your editorial aura read";
    if (scanning) return SCAN_STAGES[stage];
    return "Get a deeper read on your vibe";
  }, [aura, scanning, stage]);

  const body = (
    <div className="mx-auto w-full max-w-md px-5 pt-6 pb-12 animate-fade-in">
      <div className="mb-4 flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <span className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-primary">
          — AURA SCORE
        </span>
        <span className="w-9" />
      </div>

      <div className="text-center">
        <h1 className="font-display text-2xl font-bold tracking-tight">Aura Score</h1>
        <p className="mt-1 text-sm text-muted-foreground">{headerSubtitle}</p>
      </div>

      {/* Photo with scanning overlay */}
      <div className="relative mt-5 overflow-hidden rounded-3xl border border-primary/20 bg-card shadow-elevated">
        <div className="relative aspect-[3/4] w-full">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt="Your try-on"
              className={cn(
                "h-full w-full object-cover transition-all duration-500",
                scanning && "scale-[1.02]",
              )}
            />
          ) : (
            <div className="h-full w-full animate-pulse bg-muted" />
          )}

          {/* Scanning overlay */}
          {scanning && (
            <>
              {/* Soft tint */}
              <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-primary/10" />

              {/* Horizontal scan line */}
              <div className="aura-scanline pointer-events-none absolute inset-x-0 h-24" />

              {/* Corner brackets */}
              <div className="pointer-events-none absolute inset-3">
                <span className="absolute left-0 top-0 h-5 w-5 border-l-2 border-t-2 border-primary" />
                <span className="absolute right-0 top-0 h-5 w-5 border-r-2 border-t-2 border-primary" />
                <span className="absolute bottom-0 left-0 h-5 w-5 border-b-2 border-l-2 border-primary" />
                <span className="absolute bottom-0 right-0 h-5 w-5 border-b-2 border-r-2 border-primary" />
              </div>

              {/* Grid shimmer */}
              <div
                className="pointer-events-none absolute inset-0 opacity-20 mix-blend-overlay"
                style={{
                  backgroundImage:
                    "linear-gradient(to right, hsl(var(--primary)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--primary)) 1px, transparent 1px)",
                  backgroundSize: "32px 32px",
                }}
              />

              {/* Bottom status pill */}
              <div className="absolute inset-x-4 bottom-4 flex items-center justify-center">
                <div className="flex items-center gap-2 rounded-full border border-primary/30 bg-background/80 px-4 py-2 text-xs font-semibold text-foreground backdrop-blur-md">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                  {SCAN_STAGES[stage]}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* CTA fallback (if auto-start hasn't fired or scan finished without result) */}
      {!aura && !scanning && (
        <Button
          onClick={runAura}
          className="mt-5 h-12 w-full gradient-primary text-primary-foreground"
        >
          <Wand2 className="mr-2 h-4 w-4" />
          Read my aura · 0.10 credits
        </Button>
      )}

      {/* Result card */}
      {aura && typeof aura.aura_score === "number" && (
        <div className="mt-6 animate-fade-in rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-5 shadow-soft">
          <div className="flex flex-col items-center text-center">
            <AuraScoreRing score={aura.aura_score} size={140} />
            {aura.aura_verdict && (
              <p className="mt-4 max-w-xs text-sm leading-relaxed text-foreground/90">
                "{aura.aura_verdict}"
              </p>
            )}
          </div>

          <div className="mt-6 space-y-2.5">
            {SUB_LABELS.map(([key, label]) => {
              const v = aura[key] as number | null;
              if (typeof v !== "number") return null;
              return (
                <div key={key}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-semibold tabular-nums">{v}</span>
                  </div>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-primary to-primary/60 transition-all"
                      style={{ width: `${Math.max(0, Math.min(100, v))}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {Array.isArray(aura.aura_tips) && aura.aura_tips.length > 0 && (
            <div className="mt-6">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
                <Sparkles className="h-3.5 w-3.5" />
                Aura Tips
              </p>
              <ul className="space-y-2">
                {aura.aura_tips.map((t, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Button
            variant="outline"
            className="mt-6 h-11 w-full"
            onClick={() => navigate(`/app/result/${tryon?.id}`)}
          >
            Back to result
          </Button>
        </div>
      )}

      <BuyCreditsDialog open={buyOpen} onOpenChange={setBuyOpen} defaultPackId={buyDefaultPack} />

      <PaywallModal
        open={!!paywall}
        variant={paywall}
        onClose={() => setPaywall(null)}
        onDismiss={handlePaywallDismiss}
        onBuy={handlePaywallBuy}
      />
    </div>
  );

  return (
    <>
      <SEO title="AuraScore · FitAura AI" description="AI fashion and style score for your outfit." noindex />
      {isMobile ? <MobileShell title="Aura Score">{body}</MobileShell> : body}
    </>
  );
};

export default AuraScore;
